import _ from 'lodash';
import Big from 'big.js';
import React from 'react';
import { useFormikContext } from 'formik';
import {
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from '@material-ui/core';
import api from '../../api';
import computeFunctions from '../../../shared/compute-functions';
import {
	returnLineAmount,
	returnLineUnitPrice
} from '../../../shared/invoice-return-amount';
import InvoiceTableCell from '../Invoice/InvoiceTableCell';

const ROWS_FIELD = 'returnLineRows';
const PARTY_FIELDS = [
	'storeId',
	'partyType',
	'systemParty',
	'partyName',
	'partyIdNumber',
	'partyId',
	'partyAccountId'
];
const RELATION_PREFIXES = ['store_', 'client_', 'vendor_', 'account_'];
const LINE_FIELDS = [
	'id',
	'invoiceLineId',
	'itemId',
	'item_itemCode',
	'description',
	'karat',
	'saleCategory',
	'valueAddedTax',
	'withholdingTax',
	'quantity',
	'amount'
];
const rowsCellOptions = { invoiceLinesField: ROWS_FIELD };

const toNumber = value => {
	const number = Number(value);
	return Number.isFinite(number) ? number : 0;
};
const minus = (a, b) =>
	Big(toNumber(a))
		.minus(toNumber(b))
		.toNumber();
const lineTotal = line =>
	computeFunctions.invoiceReturnLine.total.formulate({
		...line,
		amount: toNumber(line.amount)
	});
const hasMoreThanTwoDecimals = value =>
	!Big(toNumber(value))
		.times(100)
		.mod(1)
		.eq(0);
const hasReturnValue = row => toNumber(row.quantity) !== 0;

const withDerivedAmount = (row, lineInfo) =>
	lineInfo
		? {
				...row,
				amount: returnLineAmount({
					quantity: toNumber(row.quantity),
					soldQuantity: lineInfo.soldQuantity,
					soldAmount: lineInfo.soldAmount,
					returnedQuantity: lineInfo.returnedQuantity,
					returnedAmount: lineInfo.returnedAmount
				})
		  }
		: row;

function toReturnLine(row) {
	const returnLine = _.pick(row, LINE_FIELDS);
	if (!returnLine.id) delete returnLine.id;
	returnLine.quantity = toNumber(row.quantity);
	returnLine.amount = toNumber(row.amount);
	returnLine.total = lineTotal(returnLine);
	return returnLine;
}

function invoiceLineRow(invoiceLine) {
	return {
		invoiceLineId: invoiceLine.id,
		itemId: invoiceLine.itemId,
		item_itemCode: invoiceLine.item_itemCode,
		description: invoiceLine.description,
		karat: invoiceLine.karat,
		saleCategory: invoiceLine.saleCategory,
		valueAddedTax: invoiceLine.valueAddedTax,
		withholdingTax: invoiceLine.withholdingTax,
		quantity: 0,
		amount: 0
	};
}

const orderedInvoiceLines = invoice =>
	_.sortBy(invoice.invoiceLines || [], ['createdDate', 'id']);

async function loadNewReturnSource(invoiceId) {
	const [{ data: prefill }, { data: invoice }] = await Promise.all([
		api.get(`/api/invoicereturn/prefill/${invoiceId}`),
		api.get(`/api/invoice/${invoiceId}`)
	]);
	const info = {};
	prefill.returnLines.forEach(line => {
		info[line.invoiceLineId] = {
			soldQuantity: line.soldQuantity,
			soldAmount: line.soldAmount,
			unitPrice: line.unitPrice,
			returnedQuantity: line.alreadyReturnedQuantity,
			returnedAmount: line.alreadyReturnedAmount,
			remainingQuantity: line.remainingQuantity,
			remainingAmount: line.remainingAmount
		};
	});
	const relationValues = _.pickBy(invoice, (value, key) =>
		RELATION_PREFIXES.some(prefix => key.startsWith(prefix))
	);
	return {
		info,
		headerValues: {
			...relationValues,
			..._.pick(prefill, PARTY_FIELDS),
			invoice_invoiceNumber: prefill.invoice_invoiceNumber
		},
		rows: _.sortBy(prefill.returnLines, line =>
			_.findIndex(orderedInvoiceLines(invoice), { id: line.invoiceLineId })
		).map(line => _.pick(line, LINE_FIELDS))
	};
}

async function loadSavedReturnSource(invoiceId, persisted) {
	const [{ data: invoice }, { data: returned }] = await Promise.all([
		api.get(`/api/invoice/${invoiceId}`),
		api.get(`/api/invoicereturn/returned/${invoiceId}`)
	]);
	const ownLines =
		persisted.status === 'active'
			? _.keyBy(persisted.returnLines, 'invoiceLineId')
			: {};
	const info = {};
	const invoiceLines = orderedInvoiceLines(invoice);
	invoiceLines.forEach(invoiceLine => {
		const returnedLine = returned.lines[invoiceLine.id] || {};
		const ownLine = ownLines[invoiceLine.id] || {};
		const returnedQuantity = minus(returnedLine.quantity, ownLine.quantity);
		const returnedAmount = minus(returnedLine.amount, ownLine.amount);
		info[invoiceLine.id] = {
			soldQuantity: invoiceLine.quantity,
			soldAmount: invoiceLine.amount,
			unitPrice: returnLineUnitPrice({
				soldQuantity: invoiceLine.quantity,
				soldAmount: invoiceLine.amount
			}),
			returnedQuantity,
			returnedAmount,
			remainingQuantity: minus(invoiceLine.quantity, returnedQuantity),
			remainingAmount: minus(invoiceLine.amount, returnedAmount)
		};
	});
	const savedLines = _.keyBy(persisted.returnLines, 'invoiceLineId');
	const rows = invoiceLines.map(invoiceLine =>
		savedLines[invoiceLine.id]
			? _.pick(savedLines[invoiceLine.id], LINE_FIELDS)
			: invoiceLineRow(invoiceLine)
	);
	return { info, headerValues: {}, rows };
}

function rowWarning(row, lineInfo) {
	const quantity = toNumber(row.quantity);
	const amount = toNumber(row.amount);
	if (quantity < 0)
		return 'Weight cannot be negative (لا يمكن أن يكون الوزن سالبًا)';
	if (hasMoreThanTwoDecimals(quantity))
		return 'Weight can have at most 2 decimal places (يسمح بخانتين عشريتين فقط)';
	if (
		lineInfo &&
		(quantity > toNumber(lineInfo.remainingQuantity) ||
			amount > toNumber(lineInfo.remainingAmount))
	)
		return 'Exceeds what remains to return (يتجاوز المتبقي)';
	if (quantity > 0 && !(amount > 0))
		return 'Weight is too small to be worth anything at the sold unit price (الوزن أقل من أن تكون له قيمة)';
	if (lineInfo && hasReturnValue(row)) {
		const weightLeft = minus(lineInfo.remainingQuantity, quantity);
		const amountLeft = minus(lineInfo.remainingAmount, amount);
		if (weightLeft > 0 !== amountLeft > 0)
			return `Leaves ${weightLeft} weight and ${amountLeft} amount that can never be returned; return the remaining weight and amount together or leave some of both (يجب إرجاع المتبقي من الوزن والقيمة معًا)`;
	}
	return null;
}

function InvoiceReturnLines({ readOnly }) {
	const {
		values,
		initialValues,
		setValues,
		setFieldValue
	} = useFormikContext();
	const { invoiceId } = values;
	const recordId = initialValues.id;
	const persisted = recordId
		? {
				status: initialValues.status,
				returnLines: (initialValues.returnLines || []).map(line =>
					_.pick(line, LINE_FIELDS)
				)
		  }
		: null;
	const sourceKey = invoiceId
		? JSON.stringify({ invoiceId, recordId, persisted })
		: null;
	const [source, setSource] = React.useState({ key: null });
	const initializedFor = React.useRef(null);

	React.useEffect(() => {
		if (!sourceKey) return;
		let active = true;
		const load = recordId
			? loadSavedReturnSource(invoiceId, persisted)
			: loadNewReturnSource(invoiceId);
		load
			.then(loaded => {
				if (active) setSource({ key: sourceKey, ...loaded });
			})
			.catch(() => {
				if (active) setSource({ key: sourceKey, failed: true });
			});
		return () => {
			active = false;
		};
	}, [sourceKey]);

	const sourceLoaded = source.key && source.key === sourceKey;
	const sourceFailed = sourceLoaded && source.failed;
	const rows = values[ROWS_FIELD];

	React.useEffect(() => {
		if (readOnly || !sourceLoaded) return;
		if (rows && initializedFor.current === sourceKey) return;
		initializedFor.current = sourceKey;
		setValues({
			...values,
			...(source.headerValues || {}),
			[ROWS_FIELD]: source.rows || []
		});
	}, [readOnly, sourceLoaded, sourceKey, !rows]);

	const info = (sourceLoaded && source.info) || {};
	const derivedRows = rows
		? rows.map(row => withDerivedAmount(row, info[row.invoiceLineId]))
		: null;

	React.useEffect(() => {
		if (readOnly || !derivedRows) return;
		const returnLines = derivedRows.filter(hasReturnValue).map(toReturnLine);
		if (!_.isEqual(returnLines, values.returnLines))
			setFieldValue('returnLines', returnLines);
		const totalPrice = returnLines
			.reduce((sum, returnLine) => sum.plus(returnLine.total), Big(0))
			.toNumber();
		if (totalPrice !== values.totalPrice)
			setFieldValue('totalPrice', totalPrice);
	}, [readOnly, JSON.stringify(derivedRows)]);

	if (!invoiceId) return null;
	if (!sourceLoaded) return <CircularProgress />;
	if (sourceFailed && !readOnly)
		return (
			<Typography color="error">
				Unable to load the lines of the original invoice
			</Typography>
		);

	const editing = !readOnly && rows;
	const displayed = editing ? derivedRows : values.returnLines || [];
	const cell = value => <Typography>{value == null ? '' : value}</Typography>;
	return (
		<TableContainer>
			<Table aria-label="invoice return lines">
				<TableHead>
					<TableRow>
						<TableCell>Item</TableCell>
						<TableCell>Sold Weight</TableCell>
						<TableCell>Sold Amount</TableCell>
						<TableCell>Unit Price / g</TableCell>
						<TableCell>Already Returned Weight</TableCell>
						<TableCell>Already Returned Amount</TableCell>
						<TableCell>Returning Weight</TableCell>
						<TableCell>Returning Amount</TableCell>
						<TableCell>Total</TableCell>
						{editing ? <TableCell /> : null}
					</TableRow>
				</TableHead>
				<TableBody>
					{displayed.map((row, lineIndex) => {
						const lineInfo = info[row.invoiceLineId];
						const warning = editing ? rowWarning(row, lineInfo) : null;
						return (
							<TableRow key={row.invoiceLineId || lineIndex}>
								<TableCell>
									{cell(
										_.compact([row.item_itemCode, row.description]).join(' - ')
									)}
								</TableCell>
								<TableCell>{cell(lineInfo?.soldQuantity)}</TableCell>
								<TableCell>{cell(lineInfo?.soldAmount)}</TableCell>
								<TableCell>{cell(lineInfo?.unitPrice)}</TableCell>
								<TableCell>{cell(lineInfo?.returnedQuantity)}</TableCell>
								<TableCell>{cell(lineInfo?.returnedAmount)}</TableCell>
								{editing ? (
									<>
										<InvoiceTableCell
											field="quantity"
											type="decimal"
											lineIndex={lineIndex}
											invoiceLineOptions={rowsCellOptions}
											record={values}
											readOnly={false}
										/>
										<TableCell>
											<Typography
												data-testid={`${ROWS_FIELD}.${lineIndex}.amount`}
											>
												{row.amount}
											</Typography>
										</TableCell>
									</>
								) : (
									<>
										<TableCell>{cell(row.quantity)}</TableCell>
										<TableCell>{cell(row.amount)}</TableCell>
									</>
								)}
								<TableCell>
									{cell(
										hasReturnValue(row) ? lineTotal(row) : row.total || null
									)}
								</TableCell>
								{editing ? (
									<TableCell>
										{warning ? (
											<Typography color="error" variant="body2">
												{warning}
											</Typography>
										) : null}
									</TableCell>
								) : null}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

export default InvoiceReturnLines;
